import React from "react";
import t from "../../../../../../i18n";
import { css } from "@linaria/core";
import { type FaultEvent } from "../../../../../../api/useFaultEvents";
import Text from "@ui/Text";
import { color } from "@ui";
import { spacings } from "@ui/Theme";
import type { Project } from "../../../../../../api/useProjects";
import formatDate from "@ui/i18n/formatDate";
import { getEventStatusText } from "../../../utils/faultEventStatus";
import { useTranslations } from "../../../../../../api/useTranslations";
import { getFaultStatusText } from "../../../../Fault";
import { useFaultEventsDashboards } from "../../../../../../api/useFaultEventsDashboards";
import { useComments } from "../../../../../../api/useComments";

interface ModalFormMergeEventProps {
  project: Project;
  targetEvent: FaultEvent;
  targetEventName: string;
  sourceEvent: FaultEvent;
}

const ModalFormMergeEventSummary = ({
  project,
  targetEvent,
  targetEventName,
  sourceEvent,
}: ModalFormMergeEventProps) => {
  const translation = useTranslations();

  const projectComments = useComments(project.id);
  const projectFaultEventDashboards = useFaultEventsDashboards(project.id);
  const sourceAndTargetDashboardCount = new Set(
    projectFaultEventDashboards
      .filter(
        dashboard =>
          dashboard.faultEventId === targetEvent.id || dashboard.faultEventId === sourceEvent.id,
      )
      .map(dashboard => dashboard.dashboardId),
  ).size;

  const { length: sourceAndTargetEventComments } = projectComments.filter(
    comment => comment.faultEventId === targetEvent.id || comment.faultEventId === sourceEvent.id,
  );

  const formattedStartDate = formatDate(
    new Date(Math.min(targetEvent.startsAt.getTime(), sourceEvent.startsAt.getTime())),
    "slash",
    project.timezone,
  );
  const formattedEndDate = formatDate(
    new Date(Math.max(targetEvent.endsAt.getTime(), sourceEvent.endsAt.getTime())),
    "slash",
    project.timezone,
  );

  return (
    <>
      <Text type="body1SemiBold" className={summary}>
        {t("project.fault_event.modal.form.merge_event.summary.title")}
      </Text>

      <Text type="body1SemiBold" className={summaryItem}>
        {t(
          "project.fault_event.modal.form.merge_event.summary.name",
          <Text as="span">{targetEventName}</Text>,
        )}
      </Text>
      <Text type="body1SemiBold" className={summaryItem}>
        {t(
          "project.fault_event.modal.form.merge_event.summary.date",
          <Text as="span">
            {formattedStartDate} - {formattedEndDate}
          </Text>,
        )}
      </Text>
      <Text type="body1SemiBold" className={summaryItem}>
        {t(
          "project.fault_event.modal.form.merge_event.summary.event_status",
          <Text as="span">
            {getEventStatusText(
              targetEvent.metadata.status === "TO_BE_ANALYSED"
                ? "ONGOING"
                : targetEvent.metadata.status,
            )}
          </Text>,
        )}
      </Text>
      <Text type="body1SemiBold" className={summaryItem}>
        {t(
          "project.fault_event.modal.form.merge_event.summary.reparability",
          <Text as="span">
            {targetEvent.reparability === null ? (
              <Text as="span"> - </Text>
            ) : (
              <Text as="span"> {t("reparability", targetEvent.reparability, translation)}</Text>
            )}
          </Text>,
        )}
      </Text>
      <Text type="body1SemiBold" className={summaryItem}>
        {t(
          "project.fault_event.modal.form.merge_event.summary.newDiagnosesStatus",
          <Text as="span">{getFaultStatusText(targetEvent.metadata.newDiagnosesStatus)}</Text>,
        )}
      </Text>
      <Text type="body1SemiBold" className={summaryItem}>
        {t(
          "project.fault_event.modal.form.merge_event.summary.comments",
          <Text as="span">{sourceAndTargetEventComments}</Text>,
          sourceAndTargetEventComments,
        )}
      </Text>
      <Text type="body1SemiBold" className={summaryItem}>
        {t(
          "project.fault_event.modal.form.merge_event.summary.dashboards",
          <Text as="span">{sourceAndTargetDashboardCount}</Text>,
          sourceAndTargetDashboardCount,
        )}
      </Text>
    </>
  );
};

export default ModalFormMergeEventSummary;

//#region CSS
const summary = css`
  color: ${color.carbon800};
  margin-bottom: ${spacings.xs};
`;
const summaryItem = css`
  padding-top: ${spacings.xs};
`;
//#endregion
